import { getEnv } from '@/constants';
import loggerService from '@/services/logger.service';
import store from '@/store';
import { replaceRoute } from '@/utils/routerAccess';

/**
 * Lightweight fetcher for the backend-ha (High Availability) service.
 *
 * The backend-ha service is a dedicated, horizontally-scaled backend tier
 * optimized for live event traffic where reliability and uptime are critical
 * (e.g. Agora user UID bulk-create during live sessions). Routing these
 * high-volume, latency-sensitive calls away from the main backend keeps the
 * live event path resilient even when the primary backend is under load.
 *
 * Mirrors the content fetcher interceptor behavior (auth header injection,
 * 401 retry with token refresh, error normalization) but targets
 * VUE_APP_BACKEND_HA_URL instead of VUE_APP_CL_BACKEND_URL.
 *
 * Use this fetcher only for endpoints that are explicitly served by the
 * backend-ha service; default to the standard backend fetcher otherwise.
 */

function buildUrl(baseUrl: string, url: string, queryParams?: Record<string, string | undefined>): string {
  let finalUrl = `${baseUrl}${url}`;
  if (queryParams && Object.keys(queryParams).length) {
    const params = new URLSearchParams();
    Object.entries(queryParams).forEach(([key, value]) => {
      if (value !== undefined) {
        params.append(key, value);
      }
    });
    finalUrl += `?${params.toString()}`;
  }
  return finalUrl;
}

export interface BackendHaFetcherError extends Error {
  statusCode: number;
  apiErrorCode?: unknown;
  detail?: unknown;
  error?: unknown;
  details?: unknown;
}

function createError(
  message: string,
  statusCode: number,
  extra?: Pick<BackendHaFetcherError, 'apiErrorCode' | 'detail' | 'error' | 'details'>
): BackendHaFetcherError {
  const error = new Error(message) as BackendHaFetcherError;
  error.name = '';
  error.statusCode = statusCode;
  if (extra) {
    (Object.keys(extra) as (keyof typeof extra)[]).forEach(key => {
      const value = extra[key];
      if (value !== undefined) {
        error[key] = value;
      }
    });
  }
  return error;
}

async function handleServerError(response: Response): Promise<never> {
  let error: Error;
  try {
    error = createError(JSON.stringify(await response.json()), response.status);
  } catch {
    error = createError('Internal server error', response.status);
  }
  throw error;
}

async function handleClientError(response: Response): Promise<never> {
  const data = await response.json();
  throw createError(data?.message ?? data?.detail ?? JSON.stringify(data), response.status, {
    apiErrorCode: data?.code,
    detail: data?.detail,
    error: data?.error,
    details: data?.details
  });
}

async function handle401Retry(response: Response, doFetch: () => Promise<Response>): Promise<Response> {
  let responseObj: Record<string, unknown> = {};
  try {
    responseObj = await response.json();
  } catch {
    // ignore parse errors
  }
  await store.dispatch('fetchAccessToken', { isUnauthorized: true, error: responseObj.error }, { root: true });

  const retryResponse = await doFetch();

  if (retryResponse.status === 401) {
    const eventId = store.state.event.event.id;
    replaceRoute({ name: 'auth', query: { eventID: eventId } });
    throw createError(JSON.stringify(await retryResponse.json().catch(() => ({}))), retryResponse.status);
  }

  return retryResponse;
}

export async function backendHaFetcher<TResponse = unknown>({
  url,
  method,
  body,
  queryParams
}: {
  url: string;
  method: string;
  body?: unknown;
  queryParams?: Record<string, string | undefined>;
}): Promise<TResponse> {
  const baseUrl = getEnv('VUE_APP_BACKEND_HA_URL');
  if (!baseUrl) {
    throw new Error('VUE_APP_BACKEND_HA_URL is not configured');
  }

  const finalUrl = buildUrl(baseUrl, url, queryParams);

  const doFetch = async (): Promise<Response> => {
    const jwtToken = store.state.user?.accessToken;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30_000);
    try {
      return await fetch(finalUrl, {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...(jwtToken && { Authorization: `Bearer ${jwtToken}` })
        },
        ...(body ? { body: JSON.stringify(body) } : {}),
        signal: controller.signal
      });
    } finally {
      clearTimeout(timeoutId);
    }
  };

  let response: Response;
  try {
    response = await doFetch();
  } catch (error) {
    loggerService.error(error as Error);
    throw error;
  }

  if (response.status === 401) {
    response = await handle401Retry(response, doFetch);
  }

  if (response.status >= 500) {
    await handleServerError(response);
  }

  if (response.status === 204) {
    return '' as unknown as TResponse;
  }

  if (response.status >= 400) {
    await handleClientError(response);
  }

  const contentType = response.headers.get('Content-Type');
  if (contentType?.startsWith('application/json')) {
    return response.json();
  }
  return response.text() as unknown as TResponse;
}
